import { inject, injectable } from 'inversify';
import { type Logger, TYPES } from '@bizzdesign/sdk-internal';
import { type LowLevelClient } from '@bizzdesign/sdk-lowlevel';
import { None, type Option, Some } from 'ts-results';
import { type Genql } from '@bizzdesign/bizzdesign-sdk-type-genql';
import {
  type CreateSkillPayload,
  type GetSkillsParameters,
  type GetSkillsResult,
  type SearchSkillsParameters,
  type ShareSkillPayload,
  type Skill,
  type SkillClientInterface,
  type SkillFilter,
  type SkillSearchResult,
  type UpdateSkillPayload,
} from './interfaces';
import {
  mapSkill,
  mapSkillSearchResult,
  mapSkillsPage,
  mapSkillSort,
  type SkillsPage,
} from './mappers/skill.mapper.util';

const LOG_CONTEXT = 'SkillClient';

/** Skills fetched per request while the listing generator is consumed. */
const DEFAULT_PAGE_SIZE = 100;

/** Everything the list and search reads return; the backend omits the two heavy fields. */
const SKILL_SUMMARY_FIELDS = {
  id: true,
  trigger: true,
  createdBy: true,
  type: true,
  name: true,
  description: true,
  scope: true,
  enabled: true,
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
} as const;

/** The full skill, as returned by the single read and by every mutation. */
const SKILL_FIELDS = {
  ...SKILL_SUMMARY_FIELDS,
  instructions: true,
  sharedWith: true,
} as const;

@injectable()
export class SkillClientImpl implements SkillClientInterface {
  constructor(
    @inject(TYPES.Logger) private readonly logger: Logger,
    @inject(TYPES.LowLevelClient)
    private readonly lowLevelClient: LowLevelClient,
  ) {}

  async getSkill(skillId: string): Promise<Option<Skill>> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Fetching skill',
      payload: { skillId },
    });

    const result = await this.lowLevelClient.query({
      skill: {
        __args: { id: skillId },
        ...SKILL_FIELDS,
      },
    });

    if (result.skill == null) {
      return None;
    }

    return Some(mapSkill(result.skill)) satisfies Option<Skill>;
  }

  async getSkills(
    parameters: GetSkillsParameters = {},
  ): Promise<GetSkillsResult> {
    const { filter, sort, pageSize = DEFAULT_PAGE_SIZE } = parameters;
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Fetching skills',
      payload: { filter, sort, pageSize },
    });

    const orderBy = mapSkillSort(sort);
    const firstPage = await this.fetchSkillsPage(filter, orderBy, 0, pageSize);

    return {
      skills: this.streamSkills(firstPage, filter, orderBy, pageSize),
      totalCount: firstPage.totalCount,
    } satisfies GetSkillsResult;
  }

  private async fetchSkillsPage(
    filter: SkillFilter | undefined,
    orderBy: Genql.SkillSort | undefined,
    offset: number,
    limit: number,
  ): Promise<SkillsPage> {
    const result = await this.lowLevelClient.query({
      skills: {
        __args: {
          ...(filter ? { filter } : {}),
          page: { offset, limit },
          ...(orderBy ? { orderBy } : {}),
        },
        skills: SKILL_SUMMARY_FIELDS,
        totalCount: true,
      },
    });

    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Fetched skills page',
      payload: {
        offset,
        count: result.skills.skills.length,
        totalCount: result.skills.totalCount,
      },
    });

    return mapSkillsPage(result.skills);
  }

  /**
   * Yields the first page, then fetches the next one whenever the previous came back full.
   * A short page means the backend has nothing left to give.
   */
  private async *streamSkills(
    firstPage: SkillsPage,
    filter: SkillFilter | undefined,
    orderBy: Genql.SkillSort | undefined,
    pageSize: number,
  ): AsyncGenerator<Skill> {
    let page = firstPage;
    let offset = 0;

    yield* page.skills;

    while (page.skills.length === pageSize) {
      offset += pageSize;
      page = await this.fetchSkillsPage(filter, orderBy, offset, pageSize);
      yield* page.skills;
    }
  }

  async searchSkills(
    parameters: SearchSkillsParameters,
  ): Promise<SkillSearchResult[]> {
    const { query, topK } = parameters;
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Searching skills',
      payload: { topK },
    });

    const result = await this.lowLevelClient.query({
      searchSkills: {
        __args: {
          query,
          ...(topK !== undefined ? { topK } : {}),
        },
        score: true,
        skill: SKILL_SUMMARY_FIELDS,
      },
    });

    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Searched skills',
      payload: { count: result.searchSkills.length },
    });

    return result.searchSkills.map(
      mapSkillSearchResult,
    ) satisfies SkillSearchResult[];
  }

  async createSkill(payload: CreateSkillPayload): Promise<Skill> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Creating skill',
      payload: { type: payload.type, scope: payload.scope },
    });

    const result = await this.lowLevelClient.mutation({
      createSkill: {
        __args: { with: payload },
        ...SKILL_FIELDS,
      },
      __typename: true,
    });

    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Created skill',
      payload: { skillId: result.createSkill.id },
    });

    return mapSkill(result.createSkill) satisfies Skill;
  }

  async updateSkill(payload: UpdateSkillPayload): Promise<Skill> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Updating skill',
      payload: { skillId: payload.id },
    });

    const result = await this.lowLevelClient.mutation({
      updateSkill: {
        __args: { with: payload },
        ...SKILL_FIELDS,
      },
      __typename: true,
    });

    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Updated skill',
      payload: { skillId: result.updateSkill.id },
    });

    return mapSkill(result.updateSkill) satisfies Skill;
  }

  async deleteSkill(skillId: string): Promise<string> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Deleting skill',
      payload: { skillId },
    });

    const result = await this.lowLevelClient.mutation({
      deleteSkill: {
        __args: { id: skillId },
      },
      __typename: true,
    });

    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Deleted skill',
      payload: { skillId },
    });

    return result.deleteSkill satisfies string;
  }

  async shareSkill(payload: ShareSkillPayload): Promise<Skill> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Sharing skill',
      payload: { skillId: payload.id, userCount: payload.userIds.length },
    });

    const result = await this.lowLevelClient.mutation({
      shareSkill: {
        __args: { with: { id: payload.id, userIds: payload.userIds } },
        ...SKILL_FIELDS,
      },
      __typename: true,
    });

    return mapSkill(result.shareSkill) satisfies Skill;
  }

  async unshareSkill(payload: ShareSkillPayload): Promise<Skill> {
    this.logger.debug({
      context: LOG_CONTEXT,
      msg: 'Unsharing skill',
      payload: { skillId: payload.id, userCount: payload.userIds.length },
    });

    const result = await this.lowLevelClient.mutation({
      unshareSkill: {
        __args: { with: { id: payload.id, userIds: payload.userIds } },
        ...SKILL_FIELDS,
      },
      __typename: true,
    });

    return mapSkill(result.unshareSkill) satisfies Skill;
  }
}
